import React, { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import toast from "react-hot-toast";
import { addLeadToBoard, updateLead } from "../../../services/crmApi";
import { salesPersonOptionLabel } from "../../../services/salesPersonApi";

const LeadFormModal = ({
  boardId,
  stages = [],
  defaultStageId = null,
  lead = null,
  consultants = [],
  salesPersons = [],
  isAdmin = false,
  onClose,
  onSave,
}) => {
  const isEditing = Boolean(lead?._id);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    columnId: defaultStageId || "",
    assignedTo: "",
    salesPerson: "",
    category: "",
    priority: "Medium",
    status: "Active",
    tags: "",
    address: "",
    expectedDealRevenue: "",
    leadSource: "",
    leadSourceOther: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || "",
        email: lead.email || "",
        phone: lead.phone || "",
        columnId: lead.column?._id || lead.column || defaultStageId || "",
        assignedTo:
          lead.assignedTo?._id || lead.assignedTo?.id || lead.assignedTo || "",
        salesPerson:
          lead.salesPerson?._id ||
          lead.salesPerson?.id ||
          lead.salesPerson ||
          "",
        category: lead.category || "",
        priority: lead.priority || "Medium",
        status: lead.status || "Active",
        tags: Array.isArray(lead.tags) ? lead.tags.join(", ") : "",
        address: lead.address || "",
        expectedDealRevenue: lead.expectedDealRevenue || "",
        leadSource: lead.leadSource || "",
        leadSourceOther: lead.leadSourceOther || "",
      });
    } else if (defaultStageId) {
      setFormData((prev) => ({ ...prev, columnId: defaultStageId }));
    }
  }, [lead, defaultStageId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const validate = () => {
    const name = formData.name.trim();
    if (!name) {
      setError("Lead name is required");
      return false;
    }
    if (name.length < 2) {
      setError("Lead name must be at least 2 characters");
      return false;
    }
    if (formData.email.trim()) {
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(formData.email.trim())) {
        setError("Please enter a valid email address");
        return false;
      }
    }
    if (formData.phone.trim()) {
      const phoneRe = /^[+()\-\s\d]{6,20}$/;
      if (!phoneRe.test(formData.phone.trim())) {
        setError(
          "Phone number can only contain digits, spaces, +, -, and parentheses (6–20 chars)",
        );
        return false;
      }
    }
    if (formData.expectedDealRevenue !== "" && formData.expectedDealRevenue !== null) {
      const num = Number(formData.expectedDealRevenue);
      if (Number.isNaN(num) || num < 0) {
        setError("Expected deal revenue must be a non-negative number");
        return false;
      }
    }
    if (formData.leadSource === "Other" && !formData.leadSourceOther.trim()) {
      setError("Please specify the lead source");
      return false;
    }
    if (!isEditing && !formData.columnId) {
      setError("Please select a stage");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      const parsedTags = formData.tags
        ? formData.tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
        : [];

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        status: formData.status,
        priority: formData.priority,
        category: formData.category.trim(),
        tags: parsedTags,
        address: formData.address.trim(),
        expectedDealRevenue:
          formData.expectedDealRevenue === "" ? 0 : formData.expectedDealRevenue,
        leadSource: formData.leadSource || "Direct",
        leadSourceOther:
          formData.leadSource === "Other"
            ? formData.leadSourceOther.trim()
            : undefined,
      };

      // Only Admin can set the working consultant (assignedTo) or the lead
      // owner (salesPerson). For SalesPerson / CRM-consultant editors we never
      // send salesPerson — the backend auto-sets them as the owner and ignores
      // any value we'd pass — and assignedTo is never auto-filled with the
      // creator, so the field is left untouched.
      if (isAdmin) {
        payload.assignedTo = formData.assignedTo || undefined;
        if (formData.salesPerson) {
          const selectedSp = salesPersons.find(
            (sp) => (sp._id || sp.id) === formData.salesPerson,
          );
          payload.salesPerson = formData.salesPerson;
          payload.salesPersonName =
            selectedSp?.name || selectedSp?.email || undefined;
        } else {
          payload.salesPerson = null;
          payload.salesPersonName = null;
        }
      }

      if (isEditing) {
        await updateLead(boardId, lead._id, payload);
        toast.success("Lead updated successfully");
      } else {
        await addLeadToBoard(boardId, {
          ...payload,
          columnId: formData.columnId,
        });
        toast.success("Lead created successfully");
      }
      onSave();
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to save lead",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col shadow-lg">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              {isEditing ? "Edit Lead" : "Add New Lead"}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              {isEditing
                ? "Update the lead details below"
                : "Create a new lead and assign ownership"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FiX size={22} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-6 space-y-4"
        >
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lead Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Full name"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="email@example.com"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+92 300 0000000"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
              />
            </div>

            {!isEditing && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stage <span className="text-red-500">*</span>
                </label>
                <select
                  name="columnId"
                  value={formData.columnId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 bg-white"
                >
                  <option value="">Select a stage...</option>
                  {stages.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.columnName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 bg-white"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 bg-white"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Interested">Interested</option>
                <option value="Not Interested">Not Interested</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleChange}
                placeholder="e.g., Bachelors, Masters, PhD"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
              />
            </div>

            {isAdmin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign Consultant
                </label>
                <select
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 bg-white"
                >
                  <option value="">Unassigned</option>
                  {consultants.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name ||
                        `${c.firstName || ""} ${c.lastName || ""}`.trim() ||
                        c.email}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {isAdmin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sales Person
                </label>
                <select
                  name="salesPerson"
                  value={formData.salesPerson}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 bg-white"
                >
                  <option value="">No owner</option>
                  {salesPersons.map((sp) => {
                    const spId = sp._id || sp.id;
                    return (
                      <option key={spId} value={spId}>
                        {salesPersonOptionLabel(sp)}
                      </option>
                    );
                  })}
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  Only Admin can change a lead's sales person.
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              placeholder="Comma-separated, e.g., scholarship, urgent, IELTS"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
            />
            <p className="text-xs text-gray-500 mt-1">
              Separate multiple tags with commas
            </p>
          </div>

          {/* Additional Fields */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">
              Additional Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="City, Country, Postal Code"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expected Deal Revenue
                </label>
                <input
                  type="number"
                  name="expectedDealRevenue"
                  value={formData.expectedDealRevenue}
                  onChange={handleChange}
                  placeholder="Amount in PKR"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lead Source
                </label>
                <select
                  name="leadSource"
                  value={formData.leadSource}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 bg-white"
                >
                  <option value="">Select source...</option>
                  <option value="Facebook">Facebook</option>
                  <option value="WhatsApp">WhatsApp</option>
                  <option value="Instagram">Instagram</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="Email">Email</option>
                  <option value="Referral">Referral</option>
                  <option value="Direct">Direct</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {formData.leadSource === "Other" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specify Source
                  </label>
                  <input
                    type="text"
                    name="leadSourceOther"
                    value={formData.leadSourceOther}
                    onChange={handleChange}
                    placeholder="Enter other source..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
                  />
                </div>
              )}
            </div>
          </div>
        </form>

        <div className="flex gap-3 p-6 border-t border-gray-200 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors disabled:opacity-50"
          >
            {loading ? "Saving..." : isEditing ? "Update Lead" : "Add Lead"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadFormModal;
